import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTrend } from "../context/TrendContext";
import "./TrendGraph.css";
import { loadRecentTrendHistory } from "../database/historianDB";

const COLORS = ["#00ff66", "#00e5ff", "#ff9800", "#ff4040", "#ffd54f"];

function TrendGraph({ tag, onClose, description, min, max }) {
  const { trendData } = useTrend();

  // ================= CONFIG =================
  const autoMin = Number(localStorage.getItem(`min_${tag}`) ?? 0);
  const autoMax = Number(localStorage.getItem(`max_${tag}`) ?? 100);
  const autoDesc = localStorage.getItem(`description_${tag}`) || "No Description";

  const finalMin = min !== undefined ? Number(min) : autoMin;
  const finalMax = max !== undefined ? Number(max) : autoMax;
  const finalDesc = description || autoDesc;

  // ================= STATE =================
  const [tags] = useState([tag]);
  const [historianData, setHistorianData] = useState([]);

  const [range, setRange] = useState(900);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [zoom, setZoom] = useState(1);

  const [cursor, setCursor] = useState(null);
  const [clickPoint, setClickPoint] = useState(null);

  const [position, setPosition] = useState({ x: 80, y: 80 });
  const [size, setSize] = useState({ width: 900, height: 520 });

  const dragging = useRef(false);
  const dragRef = useRef({ x: 0, y: 0 });

  const resizing = useRef(false);
  const resizeRef = useRef({});

  // ================= HISTORIAN =================
  useEffect(() => {
    let mounted = true;

    async function load() {
      const history = await loadRecentTrendHistory(tag, 5000);
      if (mounted) setHistorianData(history);
    }

    load();
    const interval = setInterval(load, 2000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [tag]);

  // ================= LIVE DATA =================
  const liveData = trendData?.[tag] || [];

  // ================= FORCE CONTINUOUS TREND (IMPORTANT FIX) =================
  const mergedData = useMemo(() => {
    const map = new Map();

    [...historianData, ...liveData].forEach(p => {
      map.set(p.time, p);
    });

    const sorted = Array.from(map.values()).sort((a, b) => a.time - b.time);

    if (sorted.length === 0) return [];

    const last = sorted[sorted.length - 1];

    // 🔥 KEY FIX: always extend time even if value is unchanged
    const now = Date.now();

    if (now > last.time) {
      sorted.push({
        time: now,
        value: last.value,   // keeps flat line movement
        quality: last.quality || "GOOD"
      });
    }

    return sorted;
  }, [historianData, liveData]);

  // ================= DRAG =================
  const startDrag = (e) => {
    dragging.current = true;
    dragRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  useEffect(() => {
    const move = (e) => {
      if (dragging.current) {
        setPosition({
          x: e.clientX - dragRef.current.x,
          y: e.clientY - dragRef.current.y,
        });
      }

      if (resizing.current) {
        setSize({
          width: Math.max(700, resizeRef.current.w + (e.clientX - resizeRef.current.x)),
          height: Math.max(420, resizeRef.current.h + (e.clientY - resizeRef.current.y)),
        });
      }
    };

    const up = () => {
      dragging.current = false;
      resizing.current = false;
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  // ================= GRAPH AREA =================
  const padding = { left: 70, right: 25, top: 25, bottom: 70 };

  const width = Math.max(1, size.width - padding.left - padding.right);
  const height = Math.max(1, size.height - padding.top - padding.bottom);

  // ================= TIME =================
  const now = Date.now();
  const viewportCenter = now - offsetX * 1000;
  const halfRange = ((range * 1000) / 2) / zoom;

  const startTime = viewportCenter - halfRange;
  const endTime = viewportCenter + halfRange;

  const scaleX = (t) =>
    ((t - startTime) / Math.max(endTime - startTime, 1)) * width;

  // ================= FILTER =================
  const visibleData = mergedData.filter(
    p => p.time >= startTime && p.time <= endTime
  );

  // ================= Y SCALE =================
  const values = visibleData.map(p => p.value);

  const rawMin = values.length ? Math.min(...values) : finalMin;
  const rawMax = values.length ? Math.max(...values) : finalMax;

  const paddingY = (rawMax - rawMin) * 0.1 || 1;

  const graphMin = rawMin - paddingY - offsetY;
  const graphMax = rawMax + paddingY - offsetY;

  const graphRange = Math.max(graphMax - graphMin, 0.0001);

  const scaleY = (v) =>
    height - ((v - graphMin) / graphRange) * height;

  // ================= LIVE VALUE =================
  const latest = visibleData.length
    ? Number(visibleData[visibleData.length - 1].value)
    : 0;

  // ================= ZOOM =================
  const zoomIn = () => setZoom(z => Math.min(5, z + 0.2));
  const zoomOut = () => setZoom(z => Math.max(0.5, z - 0.2));

  return (
    <div
      className="trend-container"
      style={{
        position: "absolute",
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >

      {/* HEADER */}
      <div className="trend-header" onMouseDown={startDrag}>
        <div className="trend-title">{tag}</div>

        <div className="trend-controls">
          <button onClick={() => setRange(60)}>1m</button>
          <button onClick={() => setRange(300)}>5m</button>
          <button onClick={() => setRange(1800)}>30m</button>
          <button onClick={() => setRange(3600)}>1h</button>

          <button onClick={zoomIn}>＋</button>
          <button onClick={zoomOut}>－</button>

          <button onClick={onClose}>✕</button>
        </div>
      </div>

      {/* LIVE */}
      <div style={{ textAlign: "center", color: "#00ff66" }}>
        LIVE: {latest.toFixed(2)}
      </div>

      {/* CURSOR READOUT */}
      {clickPoint && (
        <div style={{ textAlign: "center", color: "#ffaa00", fontSize: "12px" }}>
          {new Date(clickPoint.time).toLocaleString()} | Value: {clickPoint.value.toFixed(2)}
        </div>
      )}

      {/* SVG */}
      <svg
        width={width}
        height={height}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;

          const time = startTime + (x / width) * (endTime - startTime);

          const nearest = visibleData.reduce((prev, curr) =>
            Math.abs(curr.time - time) < Math.abs(prev.time - time) ? curr : prev,
            visibleData[0] || { time: 0, value: 0 }
          );

          setCursor({ x, time });
          setClickPoint(nearest);
        }}
      >

        {/* GRID */}
        {[...Array(6)].map((_, i) => (
          <line
            key={i}
            x1={0}
            x2={width}
            y1={(height / 5) * i}
            y2={(height / 5) * i}
            stroke="#222"
          />
        ))}

        {/* Y AXIS */}
        {[...Array(6)].map((_, i) => {
          const val = graphMax - ((graphMax - graphMin) / 5) * i;
          return (
            <text
              key={i}
              x={5}
              y={(height / 5) * i + 10}
              fill="#aaa"
              fontSize="11"
            >
              {val.toFixed(1)}
            </text>
          );
        })}

        {/* TREND LINE */}
        {visibleData.length > 1 && (
          <polyline
            points={visibleData
              .map(p => `${scaleX(p.time)},${scaleY(p.value)}`)
              .join(" ")}
            fill="none"
            stroke="#00ff66"
            strokeWidth="2"
          />
        )}

        {/* LIVE DOT */}
        {visibleData.length > 0 && (
          <circle
            cx={scaleX(visibleData[visibleData.length - 1].time)}
            cy={scaleY(latest)}
            r="5"
            fill="#00ff66"
          />
        )}

        {/* CURSOR LINE */}
        {cursor && (
          <line
            x1={cursor.x}
            x2={cursor.x}
            y1={0}
            y2={height}
            stroke="#777"
            strokeDasharray="4"
          />
        )}

      </svg>

      {/* X AXIS */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        color: "#aaa",
        fontSize: "11px",
      }}>
        {[...Array(6)].map((_, i) => {
          const t = startTime + ((endTime - startTime) / 5) * i;
          return <div key={i}>{new Date(t).toLocaleTimeString()}</div>;
        })}
      </div>

      {/* HORIZONTAL SCROLL */}
      <input
        type="range"
        min="0"
        max="3600"
        value={offsetX}
        onChange={(e) => setOffsetX(Number(e.target.value))}
        style={{ width: "95%", marginLeft: "10px" }}
      />

      {/* VERTICAL SCROLL */}
      <input
        type="range"
        min="-100"
        max="100"
        value={offsetY}
        onChange={(e) => setOffsetY(Number(e.target.value))}
        style={{
          position: "absolute",
          right: 5,
          top: 120,
          height: "200px",
          writingMode: "bt-lr",
          WebkitAppearance: "slider-vertical",
        }}
      />

      {/* RESIZE HANDLE */}
      <div className="trend-resize" />
    </div>
  );
}

export default TrendGraph;